// Scheduler.js - Interface de Agendamentos Smart Home v4.1
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Clock, Plus, Trash2, Power, Edit2, Play,
  Calendar, Blinds, Fan, Flame, Wind, Check, X,
  ChevronUp, ChevronDown, ToggleLeft, ToggleRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Scheduler.css';

// Todos os endpoints passaram a exigir autenticacao (antes alguns respondiam
// a qualquer pessoa, o que era o verdadeiro problema).
function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


const DAYS_OF_WEEK = [
  { id: 1 },
  { id: 2 },
  { id: 3 },
  { id: 4 },
  { id: 5 },
  { id: 6 },
  { id: 7 }
];

const DEVICE_TYPES = {
  shelly: { icon: Blinds, color: '#10b981' },
  vmc: { icon: Fan, color: '#06b6d4' },
  heatpump: { icon: Flame, color: '#ef4444' },
  ac: { icon: Wind, color: '#3b82f6' }
};

const ACTIONS = {
  shelly: [
    { value: 'open' },
    { value: 'close' },
    { value: 'position' }
  ],
  vmc: [
    { value: 'on' },
    { value: 'off' }
  ],
  heatpump: [
    { value: 'on' },
    { value: 'off' },
    { value: 'temperature' }
  ],
  ac: [
    { value: 'on' },
    { value: 'off' }
  ]
};

function Scheduler({ apiUrl, onBack }) {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [devices, setDevices] = useState({ shellys: [], vmc: null, heatpump: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [summary, setSummary] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    device_type: 'shelly',
    device_id: '',
    device_name: '',
    action: 'open',
    value: null,
    schedule_type: 'daily',
    time: '08:00',
    days: [1, 2, 3, 4, 5],
    date: '',
    enabled: true
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadSchedules();
    loadDevices();
    loadSummary();
  }, []);

  const loadSchedules = async () => {
    try {
      const response = await fetch(`${apiUrl}/schedules`, { headers: authHeaders() });
      const data = await response.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
      setError(t('agendamentos.erroCarregar'));
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      // Carregar Shellys
      const shellysRes = await fetch(`${apiUrl}/shelly/devices`, { headers: authHeaders() });
      const shellysData = await shellysRes.json();

      // Carregar Tuya (VMC e Heatpump)
      const tuyaRes = await fetch(`${apiUrl}/tuya/devices`, { headers: authHeaders() });
      const tuyaData = await tuyaRes.json();

      const vmc = tuyaData.devices?.find(d => d.device_type === 'vmc');
      const heatpump = tuyaData.devices?.find(d => d.device_type === 'heatpump');

      setDevices({
        shellys: shellysData.devices || [],
        vmc,
        heatpump
      });
    } catch (err) {
      console.error('Erro ao carregar dispositivos:', err);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await fetch(`${apiUrl}/schedules/stats/summary`, { headers: authHeaders() });
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error('Erro ao carregar summary:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!formData.name.trim()) {
      setError(t('agendamentos.nomeObrigatorio'));
      return;
    }
    if (!formData.device_id) {
      setError(t('agendamentos.selecioneDispositivo'));
      return;
    }
    if (formData.schedule_type === 'weekly' && formData.days.length === 0) {
      setError(t('agendamentos.selecioneDia'));
      return;
    }
    if (formData.schedule_type === 'once' && !formData.date) {
      setError(t('agendamentos.selecioneData'));
      return;
    }

    try {
      const url = editingSchedule
        ? `${apiUrl}/schedules/${editingSchedule.id}`
        : `${apiUrl}/schedules`;

      const method = editingSchedule ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowForm(false);
        setEditingSchedule(null);
        resetForm();
        loadSchedules();
        loadSummary();
      } else {
        setError(data.detail || t('agendamentos.erroGuardarCurto'));
      }
    } catch (err) {
      setError(t('agendamentos.erroGuardar'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('agendamentos.confirmarEliminar'))) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/schedules/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (response.ok) {
        loadSchedules();
        loadSummary();
      }
    } catch (err) {
      setError(t('agendamentos.erroEliminar'));
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/schedules/${id}/toggle`, {
        method: 'PATCH',
        headers: authHeaders(),
      });

      if (response.ok) {
        loadSchedules();
        loadSummary();
      }
    } catch (err) {
      setError(t('agendamentos.erroEstado'));
    }
  };

  const handleRunNow = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/schedules/${id}/run`, {
        method: 'POST',
        headers: authHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
      }
    } catch (err) {
      setError(t('agendamentos.erroExecutar'));
    }
  };

  const handleEdit = (schedule) => {
    setFormData({
      name: schedule.name,
      device_type: schedule.device_type,
      device_id: schedule.device_id,
      device_name: schedule.device_name || '',
      action: schedule.action,
      value: schedule.value,
      schedule_type: schedule.schedule_type,
      time: schedule.time,
      days: schedule.days || [1, 2, 3, 4, 5],
      date: schedule.date || '',
      enabled: schedule.enabled
    });
    setEditingSchedule(schedule);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      device_type: 'shelly',
      device_id: '',
      device_name: '',
      action: 'open',
      value: null,
      schedule_type: 'daily',
      time: '08:00',
      days: [1, 2, 3, 4, 5],
      date: '',
      enabled: true
    });
  };

  const getDeviceOptions = () => {
    const options = [];

    if (formData.device_type === 'shelly') {
      devices.shellys.forEach(d => {
        options.push({ id: d.id.toString(), name: d.name });
      });
    } else if (formData.device_type === 'vmc' && devices.vmc) {
      options.push({ id: devices.vmc.device_id, name: 'VMC' });
    } else if (formData.device_type === 'heatpump' && devices.heatpump) {
      options.push({ id: devices.heatpump.device_id, name: t('agendamentos.bombaCalor') });
    }

    return options;
  };

  const formatScheduleTime = (schedule) => {
    if (schedule.schedule_type === 'daily') {
      return t('agendamentos.todosOsDias', { hora: schedule.time });
    } else if (schedule.schedule_type === 'weekly') {
      const dayNames = schedule.days?.map(d => 
        t('agendamentos.diasCurtos.' + d)
      ).join(', ');
      return t('agendamentos.diasAs', { dias: dayNames, hora: schedule.time });
    } else if (schedule.schedule_type === 'once') {
      return t('agendamentos.dataAs', { data: schedule.date, hora: schedule.time });
    }
    return schedule.time;
  };

  const getActionLabel = (schedule) => {
    const actions = ACTIONS[schedule.device_type] || [];
    const action = actions.find(a => a.value === schedule.action);
    let label = action ? t('agendamentos.accoes.' + action.value) : schedule.action;
    
    if (schedule.value !== null && schedule.value !== undefined) {
      if (schedule.action === 'position') {
        label += ` ${schedule.value}%`;
      } else if (schedule.action === 'temperature') {
        label += ` ${schedule.value}°C`;
      }
    }
    
    return label;
  };

  const DeviceIcon = ({ type }) => {
    const config = DEVICE_TYPES[type];
    const Icon = config?.icon || Clock;
    return <Icon size={20} style={{ color: config?.color }} />;
  };

  if (loading) {
    return (
      <div className="scheduler-container">
        <div className="scheduler-loading">
          <Clock size={48} className="spin" />
          <p>{t('agendamentos.aCarregar')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduler-container">
      <div className="scheduler-content">
        {/* Header */}
        <div className="scheduler-header">
          <button onClick={onBack} className="btn-back">
            <ArrowLeft size={24} />
          </button>
          <div className="header-info">
            <div className="header-title">
              <Clock size={32} />
              <h1>{t('agendamentos.titulo')}</h1>
            </div>
            <span className="header-subtitle">{t('agendamentos.subtitulo')}</span>
          </div>
          <button 
            onClick={() => { resetForm(); setEditingSchedule(null); setShowForm(true); }}
            className="btn-add"
          >
            <Plus size={20} />
            {t('agendamentos.novo')}
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-value">{summary.total}</div>
              <div className="summary-label">{t('agendamentos.total')}</div>
            </div>
            <div className="summary-card active">
              <div className="summary-value">{summary.active}</div>
              <div className="summary-label">{t('agendamentos.ativos')}</div>
            </div>
            <div className="summary-card inactive">
              <div className="summary-value">{summary.inactive}</div>
              <div className="summary-label">{t('agendamentos.inativos')}</div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <X size={20} />
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSchedule ? t('agendamentos.editarTitulo') : t('agendamentos.novoTitulo')}</h2>
                <button onClick={() => setShowForm(false)} className="modal-close">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="schedule-form">
                {/* Nome */}
                <div className="form-group">
                  <label>{t('agendamentos.nome')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('agendamentos.placeholderNome')}
                  />
                </div>

                {/* Tipo de Dispositivo */}
                <div className="form-group">
                  <label>{t('agendamentos.tipoDispositivo')}</label>
                  <div className="device-type-selector">
                    {Object.entries(DEVICE_TYPES).map(([type, config]) => (
                      <button
                        key={type}
                        type="button"
                        className={`device-type-btn ${formData.device_type === type ? 'active' : ''}`}
                        onClick={() => setFormData({ 
                          ...formData, 
                          device_type: type, 
                          device_id: '',
                          action: ACTIONS[type]?.[0]?.value || 'on'
                        })}
                        style={{ '--accent-color': config.color }}
                      >
                        <config.icon size={20} />
                        {t('agendamentos.tipos.' + type)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dispositivo */}
                <div className="form-group">
                  <label>{t('agendamentos.dispositivo')}</label>
                  <select
                    value={formData.device_id}
                    onChange={e => {
                      const option = getDeviceOptions().find(o => o.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        device_id: e.target.value,
                        device_name: option?.name || ''
                      });
                    }}
                  >
                    <option value="">{t('agendamentos.selecione')}</option>
                    {getDeviceOptions().map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ação */}
                <div className="form-group">
                  <label>{t('agendamentos.accao')}</label>
                  <select
                    value={formData.action}
                    onChange={e => setFormData({ ...formData, action: e.target.value })}
                  >
                    {(ACTIONS[formData.device_type] || []).map(action => (
                      <option key={action.value} value={action.value}>
                        {t('agendamentos.accoes.' + action.value)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Valor (para posição ou temperatura) */}
                {(formData.action === 'position' || formData.action === 'temperature') && (
                  <div className="form-group">
                    <label>
                      {formData.action === 'position' ? t('agendamentos.posicaoPct') : t('agendamentos.temperaturaC')}
                    </label>
                    <input
                      type="number"
                      value={formData.value || ''}
                      onChange={e => setFormData({ ...formData, value: parseInt(e.target.value) })}
                      min={formData.action === 'position' ? 0 : 30}
                      max={formData.action === 'position' ? 100 : 70}
                    />
                  </div>
                )}

                {/* Tipo de Agendamento */}
                <div className="form-group">
                  <label>{t('agendamentos.frequencia')}</label>
                  <div className="schedule-type-selector">
                    <button
                      type="button"
                      className={`schedule-type-btn ${formData.schedule_type === 'daily' ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, schedule_type: 'daily' })}
                    >
                      {t('agendamentos.diario')}
                    </button>
                    <button
                      type="button"
                      className={`schedule-type-btn ${formData.schedule_type === 'weekly' ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, schedule_type: 'weekly' })}
                    >
                      {t('agendamentos.semanal')}
                    </button>
                    <button
                      type="button"
                      className={`schedule-type-btn ${formData.schedule_type === 'once' ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, schedule_type: 'once' })}
                    >
                      {t('agendamentos.umaVez')}
                    </button>
                  </div>
                </div>

                {/* Hora */}
                <div className="form-group">
                  <label>{t('agendamentos.hora')}</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={e => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>

                {/* Dias da Semana (para semanal) */}
                {formData.schedule_type === 'weekly' && (
                  <div className="form-group">
                    <label>{t('agendamentos.diasSemana')}</label>
                    <div className="days-selector">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.id}
                          type="button"
                          className={`day-btn ${formData.days.includes(day.id) ? 'active' : ''}`}
                          onClick={() => {
                            const newDays = formData.days.includes(day.id)
                              ? formData.days.filter(d => d !== day.id)
                              : [...formData.days, day.id].sort();
                            setFormData({ ...formData, days: newDays });
                          }}
                        >
                          {t('agendamentos.diasCurtos.' + day.id)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data (para uma vez) */}
                {formData.schedule_type === 'once' && (
                  <div className="form-group">
                    <label>{t('agendamentos.data')}</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={e => setFormData({ ...formData, date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                )}

                {/* Botões */}
                <div className="form-actions">
                  <button type="button" onClick={() => setShowForm(false)} className="btn-cancel">
                    {t('comum.cancelar')}
                  </button>
                  <button type="submit" className="btn-save">
                    <Check size={20} />
                    {editingSchedule ? t('comum.guardar') : t('agendamentos.criar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Lista de Agendamentos */}
        <div className="schedules-list">
          {schedules.length === 0 ? (
            <div className="empty-state">
              <Calendar size={64} />
              <h3>{t('agendamentos.semAgendamentos')}</h3>
              <p>{t('agendamentos.semAgendamentosDica')}</p>
              <button 
                onClick={() => { resetForm(); setShowForm(true); }}
                className="btn-add-empty"
              >
                <Plus size={20} />
                {t('agendamentos.criarAgendamento')}
              </button>
            </div>
          ) : (
            schedules.map(schedule => (
              <div 
                key={schedule.id} 
                className={`schedule-card ${!schedule.enabled ? 'disabled' : ''}`}
              >
                <div className="schedule-main">
                  <div className="schedule-icon">
                    <DeviceIcon type={schedule.device_type} />
                  </div>
                  
                  <div className="schedule-info">
                    <h3>{schedule.name}</h3>
                    <p className="schedule-device">
                      {t('agendamentos.tipos.' + schedule.device_type)}: {schedule.device_name || schedule.device_id}
                    </p>
                    <p className="schedule-action">
                      {getActionLabel(schedule)}
                    </p>
                  </div>

                  <div className="schedule-time">
                    <Clock size={16} />
                    <span>{formatScheduleTime(schedule)}</span>
                  </div>
                </div>

                <div className="schedule-actions">
                  <button
                    onClick={() => handleToggle(schedule.id)}
                    className={`btn-toggle ${schedule.enabled ? 'on' : 'off'}`}
                    title={schedule.enabled ? t('agendamentos.desativar') : t('agendamentos.ativar')}
                  >
                    {schedule.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  
                  <button
                    onClick={() => handleRunNow(schedule.id)}
                    className="btn-action btn-run"
                    title={t('agendamentos.executarAgora')}
                  >
                    <Play size={18} />
                  </button>
                  
                  <button
                    onClick={() => handleEdit(schedule)}
                    className="btn-action btn-edit"
                    title={t('agendamentos.editar')}
                  >
                    <Edit2 size={18} />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    className="btn-action btn-delete"
                    title={t('agendamentos.eliminar')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {schedule.last_run && (
                  <div className="schedule-meta">
                    Última execução: {new Date(schedule.last_run).toLocaleString('pt-PT')}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Scheduler;
